import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Video, CalendarClock, X, Loader2, ArrowRight, Check, Ban } from "lucide-react";

/**
 * Small helper that formats a Y-m-d date + HH:MM into a readable string.
 */
function fmt(d, t) {
  try {
    const dt = new Date(`${d}T${t}:00`);
    return dt.toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return `${d} ${t}`; }
}

/**
 * Renders the correct action buttons for a paid booking based on its
 * server-computed `window_state` and any pending `reschedule_request`.
 *
 * Rules (from the product spec):
 *   - terminal (completed/cancelled/refunded)  -> nothing
 *   - future                                    -> Reschedule; disabled Join
 *   - in_window                                 -> Join + Reschedule
 *   - past_incomplete                           -> Reschedule only
 */
export default function RescheduleControls({ booking, viewerRole = "client", onChange, testIdPrefix = "" }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const win = booking?.window_state;
  const req = booking?.reschedule_request;
  const iRequested = req && req.requested_by === viewerRole;
  const iMustRespond = req && req.requested_by !== viewerRole;
  const canJoin = win === "in_window";
  const canReschedule = ["future", "in_window", "past_incomplete"].includes(win) && !req;

  if (win === "terminal") {
    return (
      <div className="text-xs" style={{ color: "var(--text-secondary)" }}
           data-testid={`${testIdPrefix}terminal-note`}>
        This booking is closed.
      </div>
    );
  }

  const doRespond = async (accept) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/reschedule/respond`,
                                       { accept });
      toast.success(accept ? "Reschedule confirmed" : "Reschedule declined");
      onChange?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/reschedule/cancel`);
      toast.success("Reschedule request withdrawn");
      onChange?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={`${testIdPrefix}controls`}>
      {canJoin && (
        <button
          data-testid={`${testIdPrefix}join`}
          onClick={() => nav(`/session/${booking.id}`)}
          className="btn-primary text-sm inline-flex items-center gap-1">
          <Video className="w-4 h-4" /> Join session
        </button>
      )}
      {win === "future" && (
        <span className="text-xs" data-testid={`${testIdPrefix}future-note`}
              style={{ color: "var(--text-secondary)" }}>
          Join opens 5 min before start · {fmt(booking.date, booking.start_time)}
        </span>
      )}
      {win === "past_incomplete" && (
        <span className="text-xs" data-testid={`${testIdPrefix}past-note`}
              style={{ color: "var(--text-secondary)" }}>
          This session window has passed. Please reschedule to continue.
        </span>
      )}
      {canReschedule && (
        <button
          data-testid={`${testIdPrefix}reschedule-open`}
          onClick={() => setOpenModal(true)}
          disabled={busy}
          className="btn-outline text-sm inline-flex items-center gap-1">
          <CalendarClock className="w-4 h-4" /> Reschedule
        </button>
      )}
      {iRequested && (
        <div className="w-full mt-2 rounded-lg p-3 text-xs"
             data-testid={`${testIdPrefix}pending-outgoing`}
             style={{ background: "rgba(91,117,83,0.08)", border: "1px solid var(--border-soft)" }}>
          <div style={{ color: "var(--text-primary)" }}>
            <strong>Waiting for the {viewerRole === "counselor" ? "client" : "counselor"} to respond.</strong>
          </div>
          <div className="mt-0.5" style={{ color: "var(--text-secondary)" }}>
            Proposed: {fmt(req.new_date, req.new_start_time)}
          </div>
          <button data-testid={`${testIdPrefix}reschedule-cancel`}
                  onClick={doCancel} disabled={busy}
                  className="btn-outline text-xs mt-2 inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Withdraw
          </button>
        </div>
      )}
      {iMustRespond && (
        <div className="w-full mt-2 rounded-lg p-3 text-xs"
             data-testid={`${testIdPrefix}pending-incoming`}
             style={{ background: "rgba(230,178,58,0.14)", border: "1px solid var(--accent)" }}>
          <div style={{ color: "var(--text-primary)" }}>
            <strong>Reschedule proposed.</strong>
          </div>
          <div className="mt-0.5" style={{ color: "var(--text-secondary)" }}>
            New time: {fmt(req.new_date, req.new_start_time)}
            {req.reason ? ` · "${req.reason}"` : ""}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button data-testid={`${testIdPrefix}reschedule-accept`}
                    onClick={() => doRespond(true)} disabled={busy}
                    className="btn-primary text-xs inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Accept
            </button>
            <button data-testid={`${testIdPrefix}reschedule-decline`}
                    onClick={() => doRespond(false)} disabled={busy}
                    className="btn-outline text-xs inline-flex items-center gap-1">
              <Ban className="w-3 h-3" /> Decline
            </button>
          </div>
        </div>
      )}
      {openModal && (
        <RescheduleModal
          booking={booking}
          onClose={() => setOpenModal(false)}
          onDone={(data) => { setOpenModal(false); onChange?.(data); }}
        />
      )}
    </div>
  );
}

/**
 * Slot picker that reuses the existing /availability/slots endpoint so we
 * never double-book. Same booking id + payment carry through.
 */
function RescheduleModal({ booking, onClose, onDone }) {
  const [dateStr, setDateStr] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [slots, setSlots] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking?.counselor_id || !booking?.service_id) return;
    setLoading(true); setChosen(null);
    api.get("/availability/slots", {
      params: { counselor_id: booking.counselor_id, date: dateStr, service_id: booking.service_id },
    }).then(r => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [booking?.counselor_id, booking?.service_id, dateStr]);

  const minDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const propose = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/reschedule/request`,
                                       { new_date: dateStr, new_start_time: chosen,
                                         reason: reason.trim() || null });
      toast.success("Reschedule proposal sent");
      onDone?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="reschedule-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="label-eyebrow">Reschedule</span>
            <h3 className="font-serif-display text-2xl mt-1">Propose a new time</h3>
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              Same booking id · Payment already applied · {booking.service_name}
            </div>
          </div>
          <button data-testid="reschedule-close" onClick={onClose} className="opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">New date</label>
          <input data-testid="reschedule-date" type="date" min={minDate}
                 value={dateStr} onChange={e => setDateStr(e.target.value)}
                 className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2 text-sm" />
        </div>
        <div className="mt-4">
          <label className="label-eyebrow block mb-2">Available slots</label>
          {loading ? (
            <div className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="reschedule-slots-loading">
              <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading…
            </div>
          ) : slots.length === 0 ? (
            <div className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="reschedule-no-slots">
              No open slots on this date. Try another day.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="reschedule-slots">
              {slots.map(s => (
                <button key={s} type="button"
                  data-testid={`reschedule-slot-${s}`}
                  onClick={() => setChosen(s)}
                  className="px-3 py-1.5 rounded-full text-xs"
                  style={{
                    border: chosen === s ? "2px solid var(--brand)" : "1px solid var(--border-soft)",
                    background: chosen === s ? "rgba(91,117,83,0.08)" : "#fff",
                  }}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4">
          <label className="label-eyebrow block mb-2">Reason (optional)</label>
          <textarea data-testid="reschedule-reason" rows={2}
            value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Anything the other party should know"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-3 py-2 text-sm" />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm" data-testid="reschedule-cancel-modal">Cancel</button>
          <button onClick={propose} disabled={!chosen || busy}
                  data-testid="reschedule-submit"
                  className="btn-primary text-sm disabled:opacity-50 inline-flex items-center gap-1">
            <ArrowRight className="w-4 h-4" /> Send proposal
          </button>
        </div>
      </div>
    </div>
  );
}
